/**
 * @file token 自动刷新管理
 * @description 处理用户认证 token 的自动刷新逻辑，支持多标签页同步
 */
'use client';
import { ApiResponse } from '../services/api-types';
import { TokenCreateResult } from '@/services/personal-center/types';
import { getAuth, storeAuth, clearAuth } from './token-utils';
import { getCookie } from './cookie-utils';
import { clearProfile } from './profile-utils';
import { createClientLogger } from './logger/client-logger';

// 创建token-refresh专用的日志器
const logger = createClientLogger('token-refresh');

// Token刷新相关变量和常量
let refreshTimer: NodeJS.Timeout | null = null; // 刷新定时器
const LAST_REFRESH_KEY = 'last_token_refresh_time'; // 上次刷新时间存储键
const REFRESH_LOCK_KEY = 'token_refresh_lock'; // 刷新锁存储键
const REFRESH_LOCK_EXPIRY = 30 * 1000; // 锁的过期时间（30秒）
const DEFAULT_REFRESH_INTERVAL = 1 * 60 * 1000; // 默认刷新间隔（1分钟）
const REFRESH_BUFFER_TIME = 5 * 60 * 1000; // 刷新缓冲时间（5分钟），确保在token过期前刷新

/**
 * 清理定时器函数
 * @description 清除当前的刷新定时器，避免重复执行
 */
export const cleanupTimer = (): void => {
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
};

/**
 * 获取刷新锁
 * @description 尝试获取token刷新锁，防止多标签页同时执行刷新
 * @returns 是否获取到锁
 */
const acquireRefreshLock = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const now = Date.now();
        const windowId = Math.random().toString(36).substr(2, 9); // 生成唯一窗口ID，用于标识当前标签页
        const lockValue = JSON.stringify({
            timestamp: now,
            windowId,
        });

        // 尝试获取当前锁
        const currentLock = localStorage.getItem(REFRESH_LOCK_KEY);

        if (currentLock) {
            const { timestamp } = JSON.parse(currentLock);
            // 检查锁是否过期
            if (now - timestamp < REFRESH_LOCK_EXPIRY) {
                return false; // 锁已被其他页面持有
            }
        }

        // 尝试设置锁
        localStorage.setItem(REFRESH_LOCK_KEY, lockValue);

        // 验证锁是否设置成功，如果不成功，说明锁被其他标签页设置，当前标签页不应该刷新token
        const verifyLock = localStorage.getItem(REFRESH_LOCK_KEY);
        if (verifyLock) {
            const { windowId: storedWindowId } = JSON.parse(verifyLock);
            // 只有当存储的 windowId 与当前窗口的 windowId 相同时，才认为获取锁成功
            return storedWindowId === windowId;
        }
        return false;
    } catch (error) {
        logger.error('Error acquiring refresh lock:', error);
        return false;
    }
};

/**
 * 释放刷新锁
 * @description 释放token刷新锁，允许其他标签页执行刷新
 */
const releaseRefreshLock = (): void => {
    try {
        localStorage.removeItem(REFRESH_LOCK_KEY);
    } catch (error) {
        logger.error('Error releasing refresh lock:', error);
    }
};

/**
 * 计算下一次刷新的时间间隔
 * @description 根据token的有效期计算下一次刷新的时间间隔
 * @param expiresIn token的有效期（秒）
 * @returns 下一次刷新的时间间隔（毫秒）
 */
const calculateRefreshInterval = (expiresIn: number): number => {
    // 将expiresIn转换为毫秒，减去5分钟的缓冲时间
    const totalTime = expiresIn * 1000;
    const refreshInterval = totalTime - REFRESH_BUFFER_TIME;

    // 确保刷新间隔至少为1分钟，避免过于频繁的刷新
    return Math.max(refreshInterval, DEFAULT_REFRESH_INTERVAL);
};

/**
 * 执行token刷新
 * @description 执行token刷新操作，包括获取锁、调用刷新接口、存储新token
 * @returns 是否刷新成功
 */
export const performTokenRefresh = async (): Promise<boolean> => {
    // 检查是否在登录页面，如果是则不刷新token，同时清除本地数据
    if (typeof window !== 'undefined') {
        const pathname = window.location.pathname;
        const isLoginPage =
            pathname === '/login' || pathname.startsWith('/login/');

        if (isLoginPage) {
            logger.info('当前在登录页面，跳过token刷新');
            // 清除认证相关的数据（包括 localStorage 中的 token 和 cookie）
            clearAuth();
            clearProfile();
            return false;
        }
    }

    // 尝试获取刷新锁,如果acquireRefreshLock返回true 则说明当前窗口是第一个获取到锁的窗口,可以执行刷新操作
    // 否则说明其他窗口已经获取到锁,当前窗口不应该刷新token
    if (!acquireRefreshLock()) {
        // logger.info('Token refresh is already in progress by another tab');
        return false;
    }

    logger.info('Auto-refreshing token');
    try {
        // 获取refreshToken（优先从localStorage获取，其次从cookie获取）
        const token = getAuth();
        let tokenValue: string | undefined = token?.refreshToken;
        if (!tokenValue) {
            tokenValue = getCookie('refresh_token');
            if (!tokenValue) {
                logger.error(
                    'No refresh token found in localStorage or cookie',
                );
                clearAuth();
                clearProfile();
                return false;
            }
        }

        // 动态导入API并调用刷新接口
        const { refreshTokenApi } = await import('../services/personal-center');
        const response: ApiResponse<TokenCreateResult> | null =
            await refreshTokenApi({
                refreshToken: tokenValue,
            });

        // 结果处理
        if (response?.success && response.result) {
            // 存储新的token
            storeAuth(response.result);
            // 只在刷新成功时更新时间戳
            localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString());
            logger.info('Token refresh successful');
            return true;
        } else {
            // 刷新失败做无感知处理，令牌过期会从其他接口退出到登录
            logger.warn(
                'Token refresh failed:',
                response?.message || 'Unknown error',
            );
        }
    } catch (error) {
        // 请求异常/无令牌，刷新失败做无感知处理，令牌过期会从其他接口退出到登录
        logger.error('Token refresh error:', error);
    } finally {
        // 释放锁
        releaseRefreshLock();
    }
    return false;
};

/**
 * 设置下一次刷新
 * @description 安排下一次token刷新的时间
 * @param waitTime 等待时间（毫秒）
 */
export const scheduleNextRefresh = (waitTime: number): void => {
    cleanupTimer(); // 确保没有其他定时器运行
    logger.info(
        `Scheduling next token refresh in ${Math.ceil(waitTime / 1000 / 60)} minutes`,
    );

    refreshTimer = setTimeout(() => {
        (async () => {
            const success = await performTokenRefresh();

            // 计算下一次刷新的时间间隔
            let nextRefreshInterval = DEFAULT_REFRESH_INTERVAL;

            if (success) {
                // 如果刷新成功，从新存储的token中获取expiresIn并计算刷新间隔
                const auth = getAuth();
                if (auth?.expiresIn) {
                    nextRefreshInterval = calculateRefreshInterval(
                        auth.expiresIn,
                    );
                    logger.info(
                        `Calculated next refresh interval: ${Math.ceil(nextRefreshInterval / 1000 / 60)} minutes`,
                    );
                }
                // token刷新成功，排下一次刷新
                scheduleNextRefresh(nextRefreshInterval);
            } else {
                cleanupTimer(); // 清理当前定时器，终止调度
            }
        })();
    }, waitTime);
};

/**
 * 初始化token刷新定时器
 * @description 初始化token刷新机制，根据上次刷新时间和token有效期计算初始刷新间隔
 */
export const initializeRefreshTimer = (): void => {
    try {
        const lastRefreshTimeStr = localStorage.getItem(LAST_REFRESH_KEY);
        const now = Date.now();

        // 获取当前存储的token，用于计算初始刷新间隔
        const auth = getAuth();
        let initialRefreshInterval = DEFAULT_REFRESH_INTERVAL;

        if (auth?.expiresIn) {
            initialRefreshInterval = calculateRefreshInterval(auth.expiresIn);
            logger.info(
                `Initial refresh interval calculated from token: ${Math.ceil(initialRefreshInterval / 1000 / 60)} minutes`,
            );
        }

        if (!lastRefreshTimeStr) {
            // 首次登录，设置当前时间并安排下次刷新
            localStorage.setItem(LAST_REFRESH_KEY, now.toString());
            scheduleNextRefresh(initialRefreshInterval);
        } else {
            // 计算距离下次刷新的时间
            const lastRefreshTime = parseInt(lastRefreshTimeStr, 10);
            const elapsedTime = now - lastRefreshTime;
            const timeToNextRefresh = Math.max(
                0,
                initialRefreshInterval - elapsedTime,
            );

            scheduleNextRefresh(timeToNextRefresh);
        }
    } catch (error) {
        logger.error('Error initializing refresh timer:', error);
        // 出错时使用默认间隔
        scheduleNextRefresh(DEFAULT_REFRESH_INTERVAL);
    }
};

/**
 * 检查是否有有效的认证token
 * @description 检查本地存储和cookie中是否存在有效的refresh token
 * @returns 是否有有效的token
 */
export const hasAuthToken = (): boolean => {
    const storedAuth = getAuth();
    const cookieToken = getCookie('refresh_token');
    return !!(storedAuth?.refreshToken || cookieToken);
};
